import type { ReactNode } from 'react';
import React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { Form, FormControl } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Box, Button, Toggle, Flex } from '@itandi/itandi-bb-ui';
import Styles from './AccountSettingForm.module.scss';
import { useSessions } from '~/hooks/vendors/useSessions';
import type { VendorCompanyUpdateSelfParams } from '~/models/VendorCompany';
import { vendorCompanyEditValidationSchema } from '~/models/VendorCompany';
import { FormGroup } from '~/components/common/parts/form/FormGroup';
import SettingStyles from '../SettingStyle.module.scss';
import { FormLabel } from '~/components/common/parts/form/FormLabel';
import { updateSelf } from '~/utils/api/vendors/VendorCompany';
import { isSuccess } from '~/utils/api/api_handler';

export const AccountSettingForm: React.FC = () => {
  const { data: session, mutate: mutateSession } = useSessions().index();
  const handleSave = async (
    submitValue: VendorCompanyUpdateSelfParams
  ): Promise<void> => {
    const result = await updateSelf(submitValue);
    if (isSuccess(result)) {
      await mutateSession?.();
      toast.success('変更を保存しました');
      return;
    }
    toast.error(result.message ?? 'エラーが発生しました');
  };
  const initialValues = React.useMemo(
    (): VendorCompanyUpdateSelfParams => ({
      name: session?.vendorCompany.name ?? '',
      ceoLastName: session?.vendorCompany.ceoLastName ?? '',
      ceoFirstName: session?.vendorCompany.ceoFirstName ?? '',
      tel: session?.vendorCompany.tel ?? null,
      loginEmail: session?.vendorCompany.loginEmail ?? '',
      fax: session?.vendorCompany.fax ?? null,
      postalCode: session?.vendorCompany.postalCode ?? '',
      address: session?.vendorCompany.address ?? '',
      teamName: session?.vendorCompany.teamName ?? '',
      staffName: session?.vendorCompany.staffName ?? '',
      defaultCautionaryNoteToCompany:
        session?.vendorCompany.defaultCautionaryNoteToCompany ?? '',
      dealVendorNoteTemplate:
        session?.vendorCompany.dealVendorNoteTemplate ?? '',
      useLineNotification: session?.vendorCompany.useLineNotification ?? false
    }),
    [session]
  );
  if (session === null) {
    return null;
  }
  return (
    <div className={SettingStyles.SettingBlock}>
      <div className={Styles.AvatarIconWrapper}>
        <div>
          <img
            alt="avatar"
            className={Styles.AvatarIcon}
            src="/assets/Avatar.svg"
          />
        </div>
        <div className={Styles.AvatarName}>{session.vendorCompany.name}</div>
      </div>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSave}
        validationSchema={vendorCompanyEditValidationSchema}
      >
        {({
          values,
          handleChange,
          handleBlur,
          dirty,
          handleSubmit,
          errors,
          touched
        }: FormikProps<VendorCompanyUpdateSelfParams>): ReactNode => (
          <div className={SettingStyles.MiddleSizeBlock}>
            <FormGroup>
              <FormLabel>アカウント名</FormLabel>
              <FormControl
                isInvalid={touched.name && !!errors.name}
                name="name"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="例）イタンジ株式会社"
                type="text"
                value={values.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>代表者氏名</FormLabel>
              <div className={Styles.FormRow}>
                <div className={Styles.FormCol}>
                  <FormControl
                    isInvalid={touched.ceoLastName && !!errors.ceoLastName}
                    name="ceoLastName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="例）内装"
                    type="text"
                    value={values.ceoLastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ceoLastName}
                  </Form.Control.Feedback>
                </div>
                <div className={Styles.FormCol}>
                  <FormControl
                    isInvalid={touched.ceoFirstName && !!errors.ceoFirstName}
                    name="ceoFirstName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    placeholder="例）太郎"
                    type="text"
                    value={values.ceoFirstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.ceoFirstName}
                  </Form.Control.Feedback>
                </div>
              </div>
            </FormGroup>
            <div className={Styles.FormRow}>
              <div className={Styles.FormCol}>
                <FormLabel>代表電話番号</FormLabel>
                <FormControl
                  name="tel"
                  onChange={handleChange}
                  placeholder="例）03-1234-5678"
                  type="text"
                  value={values.tel ?? ''}
                />
              </div>
              <div className={Styles.FormCol}>
                <FormLabel>FAX番号</FormLabel>
                <FormControl
                  name="fax"
                  onChange={handleChange}
                  placeholder="例）03-1234-5678"
                  type="text"
                  value={values.fax ?? ''}
                />
              </div>
            </div>
            <FormGroup>
              <FormLabel>メールアドレス</FormLabel>
              <FormControl
                isInvalid={touched.loginEmail && !!errors.loginEmail}
                name="loginEmail"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="例）itandi.tarou@example.com"
                type="email"
                value={values.loginEmail}
              />
              <Form.Control.Feedback type="invalid">
                {errors.loginEmail}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>郵便番号</FormLabel>
              <div className={Styles.PostalCodeBlock}>
                <FormControl
                  isInvalid={touched.postalCode && !!errors.postalCode}
                  name="postalCode"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="例）1234567"
                  type="text"
                  value={values.postalCode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.postalCode}
                </Form.Control.Feedback>
              </div>
            </FormGroup>
            <FormGroup>
              <FormLabel>住所</FormLabel>
              <FormControl
                isInvalid={touched.address && !!errors.address}
                name="address"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="例）東京都港区六本木3丁目2-1 六本木グランドタワー42階"
                type="text"
                value={values.address}
              />
              <Form.Control.Feedback type="invalid">
                {errors.address}
              </Form.Control.Feedback>
            </FormGroup>
            <div className={Styles.FormRow}>
              <div className={Styles.FormCol}>
                <FormLabel>担当チーム名</FormLabel>
                <FormControl
                  isInvalid={touched.teamName && !!errors.teamName}
                  name="teamName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="例）イタンジ株式会社"
                  type="text"
                  value={values.teamName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.teamName}
                </Form.Control.Feedback>
                <p className={SettingStyles.FormDescription}>
                  メールの文面等で利用されます
                </p>
              </div>
              <div className={Styles.FormCol}>
                <FormLabel>担当者の呼び名</FormLabel>
                <FormControl
                  isInvalid={touched.staffName && !!errors.staffName}
                  name="staffName"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  placeholder="例）イタンジ株式会社"
                  type="text"
                  value={values.staffName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.staffName}
                </Form.Control.Feedback>
              </div>
            </div>
            <FormGroup>
              <FormLabel>注意事項</FormLabel>
              <FormControl
                as="textarea"
                isInvalid={
                  touched.defaultCautionaryNoteToCompany &&
                  !!errors.defaultCautionaryNoteToCompany
                }
                name="defaultCautionaryNoteToCompany"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="管理会社様にお伝えしたい注意事項を記載してください"
                rows={8}
                type="text"
                value={values.defaultCautionaryNoteToCompany}
              />
              <p className={SettingStyles.FormDescription}>
                改行、URLはマークダウンで記載可能です
                <br />
                改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です
              </p>
              <Form.Control.Feedback type="invalid">
                {errors.defaultCautionaryNoteToCompany}
              </Form.Control.Feedback>
            </FormGroup>
            <FormGroup>
              <FormLabel>共有案件メモテンプレート</FormLabel>
              <FormControl
                as="textarea"
                isInvalid={
                  touched.dealVendorNoteTemplate &&
                  !!errors.dealVendorNoteTemplate
                }
                name="dealVendorNoteTemplate"
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder="案件メモの新規入力時に利用されるテンプレートです"
                rows={8}
                type="text"
                value={values.dealVendorNoteTemplate}
              />
              <p className={SettingStyles.FormDescription}>
                改行、URLはマークダウンで記載可能です
                <br />
                改行は半角スペース2つ、URLは[リンク名](URL)で記載可能です
              </p>
              <Form.Control.Feedback type="invalid">
                {errors.dealVendorNoteTemplate}
              </Form.Control.Feedback>
            </FormGroup>
            {session?.admin && (
              <FormGroup>
                <FormLabel>通知設定</FormLabel>
                <Flex>
                  <Box>
                    <Toggle
                      checked={values.useLineNotification}
                      name="useLineNotification"
                      onChange={handleChange}
                    />
                  </Box>
                  <Box m="0.2rem">LINE通知設定の利用を許可</Box>
                </Flex>
              </FormGroup>
            )}
            <Button
              disabled={!dirty}
              onClick={(): void => handleSubmit()}
              type="button"
              variant="primary"
            >
              変更を保存
            </Button>
          </div>
        )}
      </Formik>
    </div>
  );
};
